import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../../config/api';
import { getStripe, isStripeAvailable } from '../../utils/stripe';
import { formatMoney } from '../../utils/formatMoney';

import { getDateLocale } from "../../utils/locale";
// Fallback used only until the backend config resolves; the real gate is resolved async below.
const envStripeConfigured = Boolean(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function WalletTopUpPaymentForm({ clientSecret, amount, currencySymbol, onSuccess, onError, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;

    setConfirming(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/wallet`,
        },
        redirect: 'if_required',
      });

      if (error) {
        onError(error.message || 'Payment failed. Please try again.');
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        await onSuccess();
        return;
      }

      onError('Payment was not completed. Please try again.');
    } catch (err) {
      onError(err.message || 'Payment failed. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div>
      <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#666', lineHeight: 1.5 }}>
        Pay {formatMoney(amount, { symbol: currencySymbol })} securely with your card.
      </p>
      <div style={{ marginBottom: 16 }}>
        <PaymentElement />
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={handleConfirm}
          disabled={!stripe || !elements || confirming}
          style={{
            flex: 1,
            background: !stripe || !elements || confirming ? '#e5e7eb' : '#e62e2e',
            color: !stripe || !elements || confirming ? '#9ca3af' : '#fff',
            padding: '12px 24px',
            borderRadius: 8,
            border: 'none',
            fontWeight: 600,
            cursor: !stripe || !elements || confirming ? 'not-allowed' : 'pointer',
            fontSize: 15,
          }}
        >
          {confirming ? 'Processing...' : 'Complete Payment'}
        </button>
        <button
          onClick={onCancel}
          disabled={confirming}
          style={{
            padding: '12px 16px',
            borderRadius: 8,
            border: '1px solid #e5e7eb',
            background: '#fff',
            color: '#666',
            fontWeight: 600,
            cursor: confirming ? 'not-allowed' : 'pointer',
            fontSize: 15,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function Wallet() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [clientSecret, setClientSecret] = useState(null);
  const [topUpAmount, setTopUpAmount] = useState(null);
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isStripeConfigured, setIsStripeConfigured] = useState(envStripeConfigured);

  useEffect(() => {
    let active = true;
    isStripeAvailable().then((available) => {
      if (active) setIsStripeConfigured(available);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    fetchWalletDetails();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchWalletDetails(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const pollInterval = setInterval(() => {
      fetchWalletDetails(true);
    }, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(pollInterval);
    };
  }, []);

  const fetchWalletDetails = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await api.get('/wallet');
      if (res.data) {
        setBalance(res.data.balance || 0);
        setCurrencySymbol(res.data.currencySymbol || '');
        setHistory(res.data.history || []);
      }
      return res.data;
    } catch (err) {
      console.error('Failed to fetch wallet details:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const resetTopUpState = () => {
    setClientSecret(null);
    setTopUpAmount(null);
    setCreatingIntent(false);
    setError('');
  };

  const handleStartTopUp = async () => {
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      setError('Please enter a valid amount greater than zero.');
      return;
    }

    setCreatingIntent(true);
    setError('');
    setStatusMessage('');

    try {
      const res = await api.post('/wallet/add/money', { amount: numericAmount });
      const { clientSecret: secret, paymentIntentId } = res.data || {};

      if (!secret || !paymentIntentId) {
        throw new Error(
          res.data?.message || 'Could not start payment. No Stripe payment session was returned.'
        );
      }

      setTopUpAmount(numericAmount);
      setClientSecret(secret);
      setAmount('');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to start wallet top-up.');
    } finally {
      setCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = async () => {
    const balanceBefore = balance;
    resetTopUpState();

    const walletData = await fetchWalletDetails(true);
    const newBalance = walletData?.balance ?? balanceBefore;

    if (newBalance > balanceBefore) {
      setStatusMessage('Payment successful. Your wallet balance has been updated.');
    } else {
      setStatusMessage('Payment received. Your balance will update shortly.');
    }
  };

  const topUpUnavailableMessage =
    'Wallet top-up requires card payment and is unavailable until Stripe is configured.';

  const parsedAmount = Number(amount);
  const canStartTopUp = isStripeConfigured && parsedAmount > 0 && !creatingIntent && !clientSecret;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px', height: 64, background: '#fff', borderBottom: '1px solid #eee',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/home')}>
          <div style={{ width: 40, height: 40, background: '#e62e2e', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <ellipse cx="12" cy="7" rx="8" ry="3.5" fill="white" opacity="0.95" />
              <rect x="4" y="7" width="16" height="2.5" fill="white" opacity="0.7" />
              <circle cx="8.5" cy="17" r="2.8" fill="white" />
              <circle cx="15.5" cy="17" r="2.8" fill="white" />
              <rect x="10.5" y="9.5" width="3" height="5.5" fill="white" opacity="0.8" />
            </svg>
          </div>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#111' }}>My Wallet</span>
        </div>
      </header>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 20px' }}>
        {loading ? (
          <div className="animate-pulse space-y-6">
            <div style={{ height: 160, background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)', borderRadius: 16 }}></div>
            <div style={{ height: 140, background: '#f9fafb', borderRadius: 12, border: '1px solid #f0f0f0' }}></div>
            <div style={{ height: 180, background: '#f9fafb', borderRadius: 12, border: '1px solid #f0f0f0' }}></div>
          </div>
        ) : (
          <>
            <div style={{
              background: 'linear-gradient(135deg, #e62e2e 0%, #ff4d4d 100%)',
              borderRadius: 16, padding: '30px', color: '#fff', marginBottom: '24px',
              boxShadow: '0 8px 20px rgba(230, 46, 46, 0.2)'
            }}>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>Available Balance</div>
              <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.5px' }}>{formatMoney(balance, { symbol: currencySymbol })}</div>
            </div>

            <div style={{
              background: '#fff', borderRadius: 12, padding: '24px', marginBottom: '32px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #eaeaea'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 16, color: '#111' }}>Add Money to Wallet</h3>

              {!isStripeConfigured ? (
                <>
                  <p style={{ margin: '0 0 16px 0', fontSize: 14, color: '#666', lineHeight: 1.5 }}>
                    {topUpUnavailableMessage}
                  </p>
                  <button
                    disabled
                    style={{
                      width: '100%',
                      background: '#e5e7eb',
                      color: '#9ca3af',
                      padding: '12px 24px',
                      borderRadius: 8,
                      border: 'none',
                      fontWeight: 600,
                      cursor: 'not-allowed',
                      fontSize: 15,
                    }}
                  >
                    Add Money (Unavailable)
                  </button>
                </>
              ) : clientSecret ? (
                <Elements stripe={getStripe()} options={{ clientSecret }}>
                  <WalletTopUpPaymentForm
                    clientSecret={clientSecret}
                    amount={topUpAmount}
                    currencySymbol={currencySymbol}
                    onSuccess={handlePaymentSuccess}
                    onError={setError}
                    onCancel={resetTopUpState}
                  />
                </Elements>
              ) : (
                <>
                  <p style={{ margin: '0 0 16px 0', fontSize: 14, color: '#666', lineHeight: 1.5 }}>
                    Add funds securely with your card. Your balance updates after Stripe confirms payment.
                  </p>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    disabled={creatingIntent}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '12px 14px',
                      borderRadius: 8,
                      border: '1px solid #e5e7eb',
                      fontSize: 15,
                      marginBottom: 12,
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleStartTopUp}
                    disabled={!canStartTopUp}
                    style={{
                      width: '100%',
                      background: canStartTopUp ? '#e62e2e' : '#e5e7eb',
                      color: canStartTopUp ? '#fff' : '#9ca3af',
                      padding: '12px 24px',
                      borderRadius: 8,
                      border: 'none',
                      fontWeight: 600,
                      cursor: canStartTopUp ? 'pointer' : 'not-allowed',
                      fontSize: 15,
                    }}
                  >
                    {creatingIntent ? 'Starting Payment...' : 'Add Money'}
                  </button>
                </>
              )}

              {error && (
                <p style={{ margin: '12px 0 0 0', fontSize: 14, color: '#c62828', lineHeight: 1.5 }}>
                  {error}
                </p>
              )}
              {statusMessage && (
                <p style={{ margin: '12px 0 0 0', fontSize: 14, color: '#2e7d32', lineHeight: 1.5 }}>
                  {statusMessage}
                </p>
              )}
            </div>

            <h3 style={{ fontSize: 18, color: '#111', marginBottom: 16, fontWeight: 700 }}>Transaction History</h3>
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #eaeaea', overflow: 'hidden' }}>
              {history.length > 0 ? (
                history.map((txn, index) => (
                  <div key={txn._id || index} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '16px 20px', borderBottom: index < history.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 20,
                        background: txn.type === 'credit' ? '#e8f5e9' : '#ffebee',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        {txn.type === 'credit' ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c62828" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>
                          {txn.type === 'credit' ? 'Added to Wallet' : 'Paid for Order'}
                        </div>
                        <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>
                          {new Date(txn.createdAt || Date.now()).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: txn.type === 'credit' ? '#2e7d32' : '#c62828' }}>
                      {txn.type === 'credit' ? '+' : '-'}{formatMoney(Math.abs(txn.amount), { symbol: currencySymbol })}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ padding: '30px', textAlign: 'center', color: '#888' }}>
                  No transactions yet.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
